// SPDX-License-Identifier: MIT
/**
 * Pure `qrMatrix(text) -> boolean[][]` (docs/173-SHAREABLE-CATALOG-QR.md) → SVG string
 * renderer, built for the admin "Utilities" QR generator (docs/356-ADMIN-UTILITIES-PAGE.md
 * v0's QR slice) — no new dependency (the repo already has a no-dep RN renderer,
 * `src/ui/QRCode.tsx`; this is the framework-free, DOM-free sibling a *web* consumer like
 * `apps/admin-web-mantis` can render straight into the page via `dangerouslySetInnerHTML`,
 * *and* download as a standalone `.svg` file).
 *
 * Deliberately framework-free (no DOM/`document`/`canvas` — that stays in the admin app,
 * which needs real browser APIs for the PNG-via-`<canvas>` export path) so it lives in
 * `@equipped/core` per the repo's "pure logic in packages/core" convention and is
 * unit-tested under the coverage-gated logic suite.
 *
 * The generated markup never interpolates caller-supplied text into the SVG — every visual
 * element is a `<rect>` derived purely from the boolean matrix + numeric options below, so
 * there is no injection surface even though the matrix itself was ultimately encoded from
 * an admin-entered URL.
 */

export interface QrSvgOptions {
  /** Pixel size of one QR module (a matrix cell). Default 8. */
  moduleSize?: number;
  /** Light quiet-zone border, in modules, on all four sides (docs/173's "light background
   * quiet zone always" rule — printed/scanned codes need it). Default 4 (the QR spec's own
   * recommended minimum). */
  quietZone?: number;
  /** Dark-module fill color. Default `#000000`. */
  foreground?: string;
  /** Light-module + quiet-zone fill color. Default `#ffffff`. */
  background?: string;
}

const DEFAULTS: Required<QrSvgOptions> = {
  moduleSize: 8,
  quietZone: 4,
  foreground: "#000000",
  background: "#ffffff",
};

/**
 * Render a `qrMatrix` boolean grid as a self-contained SVG document string — a solid
 * background rect (the quiet zone + light modules) plus one `<rect>` per dark module,
 * merging horizontally-adjacent dark modules in the same row into a single wider `<rect>`
 * (fewer nodes, smaller file, same pixels) rather than one `<rect>` per module.
 *
 * Never throws on a well-formed matrix; an empty/non-square matrix degrades to a minimal
 * (background-only) SVG rather than producing malformed markup.
 */
export function qrMatrixToSvg(matrix: boolean[][], opts?: QrSvgOptions): string {
  const moduleSize = Math.max(1, opts?.moduleSize ?? DEFAULTS.moduleSize);
  const quietZone = Math.max(0, opts?.quietZone ?? DEFAULTS.quietZone);
  const foreground = opts?.foreground ?? DEFAULTS.foreground;
  const background = opts?.background ?? DEFAULTS.background;

  const size = matrix.length;
  const dim = (size + quietZone * 2) * moduleSize;

  const rects: string[] = [];
  for (let y = 0; y < size; y++) {
    const row = matrix[y];
    if (!row) continue;
    let runStart = -1;
    for (let x = 0; x <= row.length; x++) {
      const dark = x < row.length && !!row[x];
      if (dark && runStart === -1) {
        runStart = x;
      } else if (!dark && runStart !== -1) {
        const px = (runStart + quietZone) * moduleSize;
        const py = (y + quietZone) * moduleSize;
        const pw = (x - runStart) * moduleSize;
        rects.push(`<rect x="${px}" y="${py}" width="${pw}" height="${moduleSize}" fill="${foreground}"/>`);
        runStart = -1;
      }
    }
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${dim}" height="${dim}" ` +
    `viewBox="0 0 ${dim} ${dim}" shape-rendering="crispEdges">` +
    `<rect x="0" y="0" width="${dim}" height="${dim}" fill="${background}"/>` +
    rects.join("") +
    `</svg>`
  );
}

/** The pixel side length {@link qrMatrixToSvg} would render at, for callers (e.g. a
 * `<canvas>`-based PNG exporter) that need to size an offscreen surface identically without
 * re-deriving the same arithmetic. */
export function qrSvgDimension(matrixSize: number, opts?: QrSvgOptions): number {
  const moduleSize = Math.max(1, opts?.moduleSize ?? DEFAULTS.moduleSize);
  const quietZone = Math.max(0, opts?.quietZone ?? DEFAULTS.quietZone);
  return (Math.max(0, matrixSize) + quietZone * 2) * moduleSize;
}
